import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useApplicationStore } from '../../stores/useApplicationStore'
import EventEndpoints from '../../endpoints/EventEndpoints'
import { IdentityRole } from '../../model/Event'

/**
 * Event selection for report pages, backed by the `?eventId=` search param so a
 * report configured for an event can be deep-linked and survives refresh.
 *
 * `onEventDates` is called with the event's start/end whenever an event is
 * chosen (including the restore on mount) so pages can seed their date pickers.
 */
export const useReportEvent = (onEventDates?: (start: Date, end: Date) => void) => {
  const { events } = useApplicationStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedEventId, setSelectedEventId] = useState<string>(searchParams.get('eventId') ?? '')
  const [eventRoles, setEventRoles] = useState<IdentityRole[]>([])
  const [loadingRoles, setLoadingRoles] = useState(false)
  const restored = useRef(false)

  const loadEvent = async (eventId: string) => {
    setSelectedEventId(eventId)
    if (!eventId) { setEventRoles([]); return }
    const event = events.find((e) => e.id.toString() === eventId)
    if (event && onEventDates) {
      onEventDates(new Date(event.startDate), new Date(event.endDate))
    }
    setLoadingRoles(true)
    try {
      const fullEvent = await EventEndpoints.getEvent(parseInt(eventId))
      setEventRoles(fullEvent.roles || [])
    } catch {
      setEventRoles([])
    } finally {
      setLoadingRoles(false)
    }
  }

  const selectEvent = (eventId: string) => {
    const next = new URLSearchParams(searchParams)
    if (eventId) next.set('eventId', eventId)
    else next.delete('eventId')
    setSearchParams(next, { replace: true })
    loadEvent(eventId)
  }

  // Restore roles and seeded dates for a deep-linked event once the event list arrives
  useEffect(() => {
    if (restored.current || !selectedEventId || events.length === 0) return
    restored.current = true
    loadEvent(selectedEventId)
  }, [events])

  return { selectedEventId, eventRoles, loadingRoles, selectEvent }
}
